import { YouTubeState } from "@/config/YoutubeState";
import { MediaPlayerStatus } from "@/models/frontend-only/MediaPlayerStatus";
import getYoutubeID from "get-youtube-id";

export const useYoutubeStore = defineStore("youtube", () => {
  let player: YT.Player | null = null;
  const state = ref<YouTubeState>(YouTubeState.Unstarted);
  const duration = ref(0);
  const currentTime = ref(0);
  const isFullScreen = ref(false);
  let pollReference: number | null = null;
  let youtubePlayerReady: Promise<void> | null = null;

  const videoUrl = ref("");

  const status = computed<MediaPlayerStatus>(() => {
    switch (state.value) {
      case YouTubeState.Unknown:
      case YouTubeState.Error:
        return MediaPlayerStatus.Error;
      case YouTubeState.Buffering:
      case YouTubeState.VideoCued:
        return MediaPlayerStatus.Loading;
      case YouTubeState.Playing:
        return MediaPlayerStatus.Playing;
      case YouTubeState.Paused:
      case YouTubeState.Ended:
        return MediaPlayerStatus.Paused;
      case YouTubeState.Unstarted:
      default:
        return MediaPlayerStatus.Unloaded;
    }
  });

  function init() {
    if (youtubePlayerReady) {
      return youtubePlayerReady;
    }

    youtubePlayerReady = new Promise((res) => {
      (window as any).onYouTubeIframeAPIReady = () => {
        pollForStatus();
        res();
      };
      loadScript("https://www.youtube.com/player_api", "youtube-api");
    });
    pollForStatus();
    return youtubePlayerReady;
  }

  function pause() {
    if (player?.pauseVideo) {
      player?.pauseVideo();
    }
  }

  function play() {
    player?.playVideo();
  }

  function toggle() {
    if (state.value === YouTubeState.Playing) {
      if (player?.pauseVideo) {
        player?.pauseVideo();
      }
    } else {
      player?.playVideo();
    }
  }

  const iframeElement: HTMLIFrameElement | null = null;

  // function requestFullScreen() {
  //   isFullScreen.value = true;
  //   if (!player) {
  //     return;
  //   }
  //   iframeElement = player.getIframe();
  //   document.querySelector("#big-youtube-wrapper")?.appendChild(iframeElement);
  //   iframeElement.classList.add("youtube-fullscreen");
  //   setNewPlayerTime(player.getCurrentTime());
  // }

  // function reduceFullScreen() {
  //   isFullScreen.value = false;
  //   if (!player) {
  //     return;
  //   }
  //   iframeElement = player.getIframe();
  //   document.querySelector("#big-youtube-wrapper")?.removeChild(iframeElement);
  //   iframeElement.classList.remove("youtube-fullscreen");
  //   document.querySelector(".youtube-player")?.appendChild(iframeElement);
  //   setNewPlayerTime(player.getCurrentTime());
  // }

  // function setNewPlayerTime(time: number) {
  //   setTimeout(() => {
  //     if (player) {
  //       seekTo(time);
  //     }
  //   }, 1000);
  // }

  function seekTo(second: number) {
    player?.seekTo(second, true);
  }

  // there appears to be no way to update currentTime/duration based on some event call from the youtube object
  // https://stackoverflow.com/questions/35787114/youtube-player-iframe-api-hooking-event-for-currenttime
  function pollForStatus() {
    pollReference = window.setInterval(() => {
      if (!player) {
        duration.value = 0;
        currentTime.value = 0;
        return;
      }
      // if (state.value !== YouTubeState.Playing) {
      //   return;
      // }
      duration.value = (player?.getDuration && player.getDuration()) || 0;
      currentTime.value =
        (player?.getCurrentTime && player.getCurrentTime()) || 0;
    }, 500);
  }

  function isYouTubeURLValid(url: string) {
    const videoId = getVideoIdFromURL(url);
    return videoId !== null;
  }

  function getVideoIdFromURL(url: string) {
    const pattern =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|v\/|e\/|watch\?v=|v=|embed\/|youtu\.be\/|\/v\/|\/e\/|\/watch\?v=))?(.{11})/i;
    const match = url.match(pattern);
    return match ? match[1] : null;
  }

  async function isVideoAvailable(url: string) {
    const videoId = getYoutubeID(url);
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=AIzaSyCvoYzJBVNlBMZfZ-9O5syJrAdddOMYNyE&part=status`,
    );

    const videoStatus = (await response.json())?.items?.[0]?.status?.embeddable;
    if (videoStatus === undefined) {
      console.error("Video in link is not available");
    }

    if (!videoStatus) {
      console.error("Video in link is not available");
    }
    return true;
  }

  function onInit(_player: YT.Player) {
    player = _player;
    videoUrl.value = (_player?.getVideoUrl && _player.getVideoUrl()) || "";
    pollForStatus();
  }

  function onDestroy() {
    player = null;
    state.value = YouTubeState.Unstarted;
    if (pollReference) {
      clearInterval(pollReference);
      pollReference = null;
    }
  }

  function ytEventOnReady(e: YT.PlayerEvent) {
    state.value = (e as any).data || YouTubeState.Buffering;
  }
  function ytEventOnError(e: YT.PlayerEvent) {
    throw e;
  }
  function ytEventOnStateChange(e: YT.PlayerEvent) {
    state.value = (e as any).data;
    if (state.value === YouTubeState.Ended) {
      emitter.emit("youtubeVideoEnded");
      //store.playNextSong();
      pollForStatus();
    }
  }

  return {
    pause,
    play,
    toggle,
    seekTo,
    onInit,
    onDestroy,
    isFullScreen,
    currentTime,
    duration,
    status,
    init,
    ytEventOnStateChange,
    ytEventOnReady,
    ytEventOnError,
    videoUrl,
    isYouTubeURLValid,
    isVideoAvailable,
  };
});
